import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronDown,
  Loader2,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Sparkles,
  Table2,
  Trash2,
  Users,
} from 'lucide-react';
import { useProspecting } from '../../context/ProspectingContext';

function NewSearchMenu({ onCreateGuided, onCreateQuick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white px-2.5 py-1.5 text-xs font-medium transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> New
        <ChevronDown className="w-3 h-3 opacity-80" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-20 overflow-hidden">
            <button
              type="button"
              onClick={() => { setOpen(false); onCreateGuided(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" /> Guided search
            </button>
            <div className="border-t border-gray-100 dark:border-gray-700" />
            <div
              role="button"
              aria-disabled="true"
              title="People search is coming soon"
              className="w-full flex items-start gap-2 px-3 py-2.5 text-left text-gray-400 dark:text-gray-500 bg-gray-50/70 dark:bg-gray-800/40 cursor-not-allowed"
            >
              <Users className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-4 text-gray-400 dark:text-gray-500">
                  People search
                </span>
                <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                  Coming soon
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); onCreateQuick('companies'); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <Building2 className="w-3.5 h-3.5 text-gray-400" /> Company search
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SearchRow({ search, active, onOpen, onDelete }) {
  const Icon = search.type === 'companies' ? Building2 : Users;
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onOpen}
        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${
          active
            ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
      >
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400'}`} />
        <span className="text-sm font-medium truncate flex-1">{search.name}</span>
        {search.filters.length > 0 && (
          <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
            active
              ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
          }`}>
            {search.filters.length}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
        aria-label="Delete search"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function SavedListRow({ list, active, onOpen }) {
  const Icon = list.entity_type === 'company' ? Building2 : Users;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${
        active
          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      }`}
    >
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-white dark:text-gray-900' : 'text-gray-400'}`} />
      <span className="text-sm font-medium truncate flex-1">{list.name}</span>
      <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
        active
          ? 'bg-white/15 text-white dark:bg-gray-900/10 dark:text-gray-900'
          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
      }`}>
        {list.record_count}
      </span>
    </button>
  );
}

function CollapsedRail({
  searches,
  searchOrder,
  activeSearchId,
  savedLists = [],
  activeSavedListId,
  onOpen,
  onOpenSavedList,
  onExpand,
  onCreateGuided,
}) {
  return (
    <aside className="w-12 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col items-center transition-[width] duration-200">
      <div className="h-12 flex items-center justify-center w-full border-b border-gray-100 dark:border-gray-700">
        <button
          type="button"
          onClick={onExpand}
          title="Expand searches"
          className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 w-full flex flex-col items-center gap-1">
        {searchOrder.map((id) => {
          const s = searches[id];
          if (!s) return null;
          const Icon = s.type === 'companies' ? Building2 : Users;
          const active = id === activeSearchId;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onOpen(id)}
              title={s.name}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                active
                  ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300'
                  : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          );
        })}
        <button
          type="button"
          onClick={onCreateGuided}
          title="New guided search"
          className="w-8 h-8 mt-1 rounded-lg flex items-center justify-center text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
        </button>
        {savedLists.length > 0 && (
          <div className="w-6 border-t border-gray-200 dark:border-gray-700 my-1" />
        )}
        {savedLists.map((list) => (
          <button
            key={list.id}
            type="button"
            onClick={() => onOpenSavedList?.(list.id)}
            title={list.name}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
              list.id === activeSavedListId
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Table2 className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
    </aside>
  );
}

export default function ProspectingSearchesPanel({
  activeSearchId,
  collapsed,
  onToggle,
  savedLists = [],
  savedListsLoading = false,
  activeSavedListId = null,
  onOpenSavedList,
  onShowResults,
}) {
  const navigate = useNavigate();
  const { searches, searchOrder, createSearch, deleteSearch } = useProspecting();

  const handleCreateQuick = (type) => {
    const id = createSearch(type);
    navigate(`/prospecting/${id}`);
  };

  const handleCreateGuided = () => {
    navigate('/prospecting/new');
  };

  const handleOpen = (id) => {
    onShowResults?.();
    navigate(`/prospecting/${id}`);
  };

  const handleDelete = (id) => {
    if (id === activeSearchId) {
      const remaining = searchOrder.filter((sid) => sid !== id);
      deleteSearch(id);
      navigate(remaining.length > 0 ? `/prospecting/${remaining[0]}` : '/prospecting');
      return;
    }
    deleteSearch(id);
  };

  if (collapsed) {
    return (
      <CollapsedRail
        searches={searches}
        searchOrder={searchOrder}
        activeSearchId={activeSearchId}
        savedLists={savedLists}
        activeSavedListId={activeSavedListId}
        onOpen={handleOpen}
        onOpenSavedList={onOpenSavedList}
        onExpand={onToggle}
        onCreateGuided={handleCreateGuided}
      />
    );
  }

  return (
    <aside className="w-72 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col transition-[width] duration-200">
      <div className="h-12 flex items-center justify-between pl-3 pr-2 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggle}
            title="Collapse searches"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Searches
          </span>
        </div>
        <NewSearchMenu onCreateGuided={handleCreateGuided} onCreateQuick={handleCreateQuick} />
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-0.5">
          {searchOrder.length === 0 ? (
            <div className="px-2 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
              No searches yet. Click "New" to start.
            </div>
          ) : (
            searchOrder.map((id) => {
              const s = searches[id];
              if (!s) return null;
              return (
                <SearchRow
                  key={id}
                  search={s}
                  active={id === activeSearchId && !activeSavedListId}
                  onOpen={() => handleOpen(id)}
                  onDelete={() => handleDelete(id)}
                />
              );
            })
          )}
        </div>
        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="px-2 mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            <Table2 className="w-3 h-3" />
            Lists
            {savedListsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
          </div>
          <div className="space-y-0.5">
            {savedLists.map((list) => (
              <SavedListRow
                key={list.id}
                list={list}
                active={list.id === activeSavedListId}
                onOpen={() => onOpenSavedList?.(list.id)}
              />
            ))}
            {!savedListsLoading && savedLists.length === 0 && (
              <div className="px-2 py-3 text-xs text-gray-400 dark:text-gray-500">
                No saved lists
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
